/**
 * Workflows DLQ admin.
 *
 * Operator surface for the failed-event queue. Reads from the
 * workflows aggregator endpoint which fans out to each producer's
 * admin DLQ surface (B2C today, MyApp once its row-change emitter
 * lands). Per-row menu actions proxy back through the aggregator:
 *
 *   POST /workflows/dlq/{producer}/{id}/requeue
 *   POST /workflows/dlq/{producer}/{id}/discard
 *
 * Paging, search AND sort are SERVER-SIDE through
 * ``GET /workflows/dlq?limit=&offset=&search=&sort_by=&sort_order=`` — the
 * aggregator forwards ONE identical triple to every producer, which applies
 * it in its own SQL (business2cloud ``/api/admin/outbox/failed``, W5 of the
 * workflow-grids-dlq-search-sort plan, superseding D7 of
 * workflow-grids-standardise). So a single pager, one SearchInput and one
 * sort state drive every producer section in lockstep: a header click in
 * either section re-sorts every producer, and each producer's ``total``
 * reflects the search. Nothing is filtered or reordered client-side — the
 * anti-pattern the standard grid bans.
 *
 * Design choices:
 *   - One row per failed event, grouped by producer (operator typically
 *     triages one producer at a time). The Producer filter picks which
 *     section is shown and which producer's total drives the pager;
 *     "All" uses the largest producer total so every row stays reachable.
 *   - Per-producer "unreachable" banner when the aggregator couldn't
 *     reach that producer — non-fatal; the rest of the response is
 *     still usable. Same posture as the aggregator backend.
 *   - Truncated payload preview on the row; full payload + last_error
 *     in the View dialog (avoids wall-of-JSON in the table).
 */

import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ScrollableTable } from "@/components/ui/scrollable-table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageHeader } from "@/components/ui/page-header";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  Inbox,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  RotateCw,
  ServerCrash,
  ShieldOff,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "sonner";
import {
  workflowsApi,
  type DlqProducerSlice,
  type DlqRow,
  type DlqSortField,
} from "@/services/api/workflows";

export const Route = createFileRoute("/portal/workflows/dlq")({
  component: DlqPage,
});

const ALL_PRODUCERS = "__all__";

function DlqPage() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const canManage = can("manage", "automation");

  // The Producer filter. Options come from the response (every producer the
  // aggregator knows about), held in state so the Select stays stable while
  // a single producer is shown.
  const [producerFilter, setProducerFilter] = useState<string>(ALL_PRODUCERS);
  const [producers, setProducers] = useState<string[]>([]);
  // Server-side search — debounced so a keystroke is not a request.
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const hasSearch = debouncedSearch.trim() !== "";
  // The aggregator's grand total across reachable producers.
  const [grandTotal, setGrandTotal] = useState(0);

  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [previewRow, setPreviewRow] = useState<DlqRow | null>(null);

  /**
   * One page of every producer's queue. The hook's ``totalCount`` is the
   * selected producer's total — or, for "All", the LARGEST producer total, so
   * the pager can reach the last page of the deepest queue (D7); those totals
   * now carry the search predicate. Search and sort come from the hook and
   * go to the server; the same triple reaches every producer.
   */
  const fetchDlq = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<DlqProducerSlice>> => {
      const res = await workflowsApi.listDlq(token ?? undefined, {
        search: debouncedSearch.trim() || undefined,
        sortBy: sort?.key as DlqSortField | undefined,
        sortOrder: sort?.direction,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setProducers(res.by_producer.map((p) => p.producer));
      setGrandTotal(res.total);
      const reachable = res.by_producer.filter((p) => p.error === null);
      const counted =
        producerFilter === ALL_PRODUCERS
          ? reachable
          : reachable.filter((p) => p.producer === producerFilter);
      const totalCount = counted.reduce((max, p) => Math.max(max, p.total), 0);
      return { data: res.by_producer, totalCount, page, pageSize };
    },
    [token, producerFilter, debouncedSearch],
  );

  const {
    data: byProducer,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<DlqProducerSlice>({
    fetchFn: fetchDlq,
    initialPageSize: 25,
    initialSort: { key: "enqueued_at", direction: "desc" },
    dependencies: [token, producerFilter, debouncedSearch],
  });

  // The slices to render: reachable ones, narrowed to the selected producer.
  // Unreachable producers always get their banner — the operator should see
  // that a producer is dark even while looking at another one.
  const visibleSlices = useMemo(
    () =>
      byProducer.filter(
        (p) =>
          p.error === null &&
          (producerFilter === ALL_PRODUCERS || p.producer === producerFilter),
      ),
    [byProducer, producerFilter],
  );
  const unreachableSlices = useMemo(
    () => byProducer.filter((p) => p.error !== null),
    [byProducer],
  );
  const visibleRowCount = useMemo(
    () => visibleSlices.reduce((n, s) => n + s.rows.length, 0),
    [visibleSlices],
  );
  // What is shown is dark — its total is unknown, not zero: the selected
  // producer is unreachable, or under "All" no producer could be reached.
  const selectedUnreachable =
    producerFilter === ALL_PRODUCERS
      ? byProducer.length > 0 && visibleSlices.length === 0
      : unreachableSlices.some((p) => p.producer === producerFilter);

  /**
   * Refetch after a row LEFT the queue (requeued or discarded). If it was the
   * only visible row and we are past page 1, step back instead of refetching
   * the same window — the hook does not move `page` when the total shrinks,
   * so a plain refresh would land on an empty page past the end. Exact for a
   * selected producer; for "All" the largest total shrinks by one at most.
   */
  const refetchAfterRowLeft = useCallback(() => {
    if (visibleRowCount === 1 && pagination.page > 1) prevPage();
    else refresh();
  }, [visibleRowCount, pagination.page, prevPage, refresh]);

  async function handleRequeue(row: DlqRow) {
    if (!confirm(`Requeue event ${row.event_id}?`)) return;
    const key = `${row.producer}:${row.id}`;
    setBusyRow(key);
    try {
      await workflowsApi.requeueDlqRow(
        row.producer,
        row.id,
        token ?? undefined,
      );
      toast.success(`Requeued ${row.event_id}`);
      refetchAfterRowLeft();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Requeue failed";
      toast.error(message);
    } finally {
      setBusyRow(null);
    }
  }

  async function handleDiscard(row: DlqRow) {
    if (
      !confirm(
        `Discard event ${row.event_id}? This is permanent — the drain ` +
          "worker will stop considering it.",
      )
    ) {
      return;
    }
    const key = `${row.producer}:${row.id}`;
    setBusyRow(key);
    try {
      await workflowsApi.discardDlqRow(
        row.producer,
        row.id,
        token ?? undefined,
      );
      toast.success(`Discarded ${row.event_id}`);
      refetchAfterRowLeft();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Discard failed";
      toast.error(message);
    } finally {
      setBusyRow(null);
    }
  }


  return (
    <>
      <Helmet>
        <title>DLQ | Workflows | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Workflows DLQ"
          description="Failed events across all producers. Requeue retries; discard removes from consideration permanently."
          icon={Inbox}
          eyebrow="Operations"
        />

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {/* Per-producer banners for unreachable ones — non-fatal but useful. */}
        {unreachableSlices.map((s) => (
          <Alert key={s.producer} variant="destructive">
            <ServerCrash className="h-4 w-4" />
            <AlertDescription>
              <strong>{s.producer}</strong> unreachable — {s.error}
            </AlertDescription>
          </Alert>
        ))}

        {/* One content panel: controls bar, one table per producer, one pager.
            Search, sort and the pager all change what the server returns —
            the Producer filter picks which section is shown and whose total
            the pager counts. */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by event id, stream or error..."
              containerClassName="flex-1 min-w-[200px]"
              aria-label="Search DLQ"
              // BE = FE: the DP 422s and the producer 400s a search over 200
              // chars (grid_params.SEARCH_MAX_LENGTH / FailedOutboxSort
              // .MaxSearchLength); cap it here so the operator never sees
              // that as an empty grid.
              maxLength={200}
            />
            <Select value={producerFilter} onValueChange={setProducerFilter}>
              <SelectTrigger className="w-[140px]" aria-label="Producer filter">
                <SelectValue className="truncate" placeholder="All producers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PRODUCERS}>All producers</SelectItem>
                {producers.map((p) => (
                  <SelectItem key={p} value={p} className="capitalize">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              {grandTotal.toLocaleString()} {hasSearch ? "matching" : "stuck"}{" "}
              across reachable producers
            </span>
            <div className="ml-auto flex items-center gap-2">
              {!canManage ? (
                <Badge variant="secondary">
                  <ShieldOff className="h-3 w-3 mr-1" />
                  Read-only
                </Badge>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={isLoading}
                aria-label="Refresh"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {isLoading && byProducer.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : visibleRowCount === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-500" />
              <h3 className="mb-2 text-lg font-medium">
                {pagination.totalCount > 0
                  ? "No failed events on this page"
                  : hasSearch && !selectedUnreachable
                    ? `No failed events match "${debouncedSearch.trim()}"`
                    : selectedUnreachable
                      ? producerFilter === ALL_PRODUCERS
                        ? "No producer is reachable"
                        : `${producerFilter} is unreachable`
                      : "Nothing stuck"}
              </h3>
              <p className="mb-4 text-muted-foreground">
                {pagination.totalCount > 0
                  ? "Step back a page — every producer shares one pager."
                  : hasSearch && !selectedUnreachable
                    ? "Clear the search to see every failed event."
                    : selectedUnreachable
                      ? "The queue could not be read — see the banner above."
                      : producerFilter === ALL_PRODUCERS
                        ? "No producer has a failed event waiting."
                        : `${producerFilter} has no failed events waiting.`}
              </p>
            </div>
          ) : (
            visibleSlices.map((slice) => (
              <section key={slice.producer} className="space-y-3">
                <h3 className="text-base font-semibold capitalize">
                  {slice.producer}{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    {slice.total.toLocaleString()} {hasSearch ? "matching" : "stuck"}
                  </span>
                </h3>
                {slice.rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {slice.total > 0
                      ? "No rows on this page — this producer's queue ends before it."
                      : hasSearch
                        ? "No failed events match the search."
                        : "Nothing stuck. ✓"}
                  </p>
                ) : (
                  <ScrollableTable>
                    <Table>
                      {/* Both sections read the ONE hook sort state: a
                          click in either re-sorts every producer, and both
                          headers show the same indicator. */}
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead
                            direction={getSortDirection("enqueued_at")}
                            onSort={() => requestSort("enqueued_at")}
                          >
                            Enqueued
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("event_id")}
                            onSort={() => requestSort("event_id")}
                          >
                            Event ID
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("stream")}
                            onSort={() => requestSort("stream")}
                          >
                            Stream
                          </SortableTableHead>
                          <SortableTableHead
                            className="text-right"
                            direction={getSortDirection("attempts")}
                            onSort={() => requestSort("attempts")}
                          >
                            Attempts
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("last_error")}
                            onSort={() => requestSort("last_error")}
                          >
                            Last error
                          </SortableTableHead>
                          <TableHead className="w-[50px]">{""}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {slice.rows.map((row) => {
                          const key = `${row.producer}:${row.id}`;
                          const busy = busyRow === key;
                          return (
                            <TableRow key={key}>
                              <TableCell className="text-xs whitespace-nowrap">
                                {formatTs(row.enqueued_at_utc)}
                              </TableCell>
                              <TableCell className="font-mono text-xs">
                                {row.event_id}
                              </TableCell>
                              <TableCell>
                                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                  {row.stream}
                                </code>
                              </TableCell>
                              <TableCell className="text-right">
                                {row.attempts}
                              </TableCell>
                              <TableCell className="text-xs max-w-[300px] truncate">
                                {row.last_error || "—"}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      disabled={busy}
                                      aria-label={`Actions for ${row.event_id}`}
                                    >
                                      {busy ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <MoreHorizontal className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => setPreviewRow(row)}
                                    >
                                      <Eye className="mr-2 h-4 w-4" />
                                      View payload
                                    </DropdownMenuItem>
                                    {canManage ? (
                                      <>
                                        <DropdownMenuItem
                                          onClick={() => void handleRequeue(row)}
                                        >
                                          <RotateCw className="mr-2 h-4 w-4" />
                                          Requeue
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="text-rose-600 focus:text-rose-700"
                                          onClick={() => void handleDiscard(row)}
                                        >
                                          <Trash2 className="mr-2 h-4 w-4" />
                                          Discard
                                        </DropdownMenuItem>
                                      </>
                                    ) : null}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollableTable>
                )}
              </section>
            ))
          )}
          {/* Outside the sections/empty ternary on purpose: a page past the
              end (its rows just requeued away) shows the empty state AND the
              pager, so the operator can step back. */}
          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={pageSizeOptions}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={previewRow !== null}
        onOpenChange={(open) => !open && setPreviewRow(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-base break-all">
              {previewRow?.event_id}
            </DialogTitle>
            <DialogDescription>
              {previewRow?.stream} · {previewRow?.producer} · attempts:{" "}
              {previewRow?.attempts}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {previewRow?.last_error ? (
              <div>
                <div className="text-xs font-medium mb-1">Last error</div>
                <div className="text-xs bg-destructive/10 text-destructive p-2 rounded">
                  {previewRow.last_error}
                </div>
              </div>
            ) : null}

            <div>
              <div className="text-xs font-medium mb-1">Payload</div>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-[400px]">
                {prettyJson(previewRow?.payload)}
              </pre>
            </div>

            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>Enqueued: {previewRow?.enqueued_at_utc}</div>
              {previewRow?.last_attempt_at_utc ? (
                <div>Last attempt: {previewRow.last_attempt_at_utc}</div>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function formatTs(ts: string | null): string {
  if (!ts) return "";
  // Backend hands back ISO strings; render in the viewer's locale so
  // operators see times in their own tz (same helper as executions.tsx —
  // a local copy by design, D16: no new shared component for one grid).
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString();
}

function prettyJson(blob: string | undefined): string {
  if (!blob) return "";
  try {
    return JSON.stringify(JSON.parse(blob), null, 2);
  } catch {
    return blob;
  }
}
